import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, RefreshCw } from "lucide-react";
import { Inbox } from "@/api/assistant";
import { apiErrorMessage } from "@/api/client";
import { toast } from "@/lib/feedback";
import { roleVar } from "@/lib/colors";
import { useInbox } from "@/lib/useInbox";
import { ProposalCard } from "./Parts";

/** How old the last inbox review may be before opening Today starts a new one. */
const AUTO_SYNC_HOURS = 12;

function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/** "From your email": the inbox review's proposals, waiting for Apply. */
export default function InboxPanel() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const inboxQuery = useInbox();
  const autoStarted = useRef(false);
  const state = inboxQuery.data;
  const running = state?.review?.status === "running";

  const sync = useMutation({
    mutationFn: (staleHours?: number) => Inbox.sync(staleHours),
    onSuccess: (s) => qc.setQueryData(["inbox"], s),
    onError: (e) => toast.err(`Couldn't check your email: ${apiErrorMessage(e)}`),
  });

  // Opening Today checks the inbox if the last review is stale.
  useEffect(() => {
    if (state?.connected && !autoStarted.current) {
      autoStarted.current = true;
      sync.mutate(AUTO_SYNC_HOURS);
    }
  }, [state?.connected, sync]);

  // When a review finishes, the deals it synced have fresh activity.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      void qc.invalidateQueries({ queryKey: ["my-work"] });
      void qc.invalidateQueries({ queryKey: ["deals"] });
      if (state?.review?.status === "error") toast.err("The email check didn't finish. Try again.");
    }
    wasRunning.current = running;
  }, [running, qc, state?.review?.status]);

  if (!state) return null;

  if (!state.connected) {
    return (
      <section className="panel p-4 flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent-azure/10 text-accent-azure">
          <Mail className="h-4 w-4" />
        </span>
        <p className="flex-1 text-md text-mut-300">
          Connect Google to let LeadZeppelin read your email and suggest updates to your deals.
        </p>
        <Link to="/settings" className="btn">
          Connect
        </Link>
      </section>
    );
  }

  return (
    <section style={roleVar("violet")}>
      <div className="mb-1.5 flex items-center gap-2">
        <h2
          className="flex items-center gap-2 text-2xs font-mono font-medium uppercase tracking-[.1em]"
          style={{ color: "rgb(var(--c))" }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: "rgb(var(--c))" }} />
          From your email
        </h2>
        <span className="text-xs text-mut-500">
          {running
            ? "Reading your new email…"
            : state.review
              ? `Checked ${ago(state.review.updated_at)}`
              : "Not checked yet"}
        </span>
        <div className="flex-1" />
        {state.review && !running && (
          <button
            className="text-xs text-mut-500 hover:text-mut-100"
            onClick={() => navigate(`/?c=${state.review!.id}`)}
          >
            Open the review
          </button>
        )}
        <button
          className="btn btn-ghost !px-2 !py-1 text-xs"
          disabled={running || sync.isPending}
          onClick={() => sync.mutate(undefined)}
          title="Check my email now"
        >
          {running ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Sync
        </button>
      </div>

      {state.pending.length > 0 ? (
        <div className="space-y-2">
          {state.pending.map((a) => (
            <ProposalCard key={a.id} action={a} />
          ))}
        </div>
      ) : (
        !running && (
          <p className="px-3 py-2 text-sm text-mut-500">
            No suggested changes. New email on your deals and possible new leads show up here.
          </p>
        )
      )}
    </section>
  );
}
